import React, { useState, useEffect, useCallback } from 'react';
import { ADMIN_API_BASE_URL } from '../config';
import { 
  FaTimes, 
  FaExclamationTriangle, 
  FaExclamation, 
  FaEye, 
  FaClock, 
  FaCheck 
} from 'react-icons/fa';

// Explicitly type the icons as React components
const FaClockIcon = FaClock as React.ComponentType<any>;
const FaCheckIcon = FaCheck as React.ComponentType<any>;
const FaTimesIcon = FaTimes as React.ComponentType<any>;
const FaExclamationTriangleIcon = FaExclamationTriangle as React.ComponentType<any>;
const FaExclamationIcon = FaExclamation as React.ComponentType<any>;
const FaEyeIcon = FaEye as React.ComponentType<any>;

interface WithdrawalRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_mobile: string;
  requested_amount: number;
  tds_amount: number;
  net_amount: number;
  bank_account_details: {
    account_holder_name: string;
    bank_name: string;
    account_number: string;
    ifsc_code: string;
    masked_account: string;
  };
  request_date: string;
  status: string;
  rejection_reason?: string;
  admin_notes?: string;
  processed_by?: string;
  processed_at?: string;
  payout_transaction_id?: string;
  user_details: {
    id: string;
    kyc_status: string;
    withdrawable_balance: number;
    total_winnings: number;
    account_status: string;
  };
}

type WithdrawalReviewModalProps = {
  withdrawalId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onWithdrawalUpdated: () => void;
};

const WithdrawalReviewModal: React.FC<WithdrawalReviewModalProps> = ({
  withdrawalId,
  isOpen,
  onClose,
  onWithdrawalUpdated
}) => {
  const [withdrawal, setWithdrawal] = useState<WithdrawalRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [customRejectionReason, setCustomRejectionReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const rejectionReasons = [
    'Insufficient balance',
    'KYC not verified',
    'Invalid bank details',
    'Fraudulent activity suspected',
    'Document verification failed',
    'Account suspended',
    'Other (specify)'
  ];

  const fetchWithdrawalDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${ADMIN_API_BASE_URL}/withdrawals/${withdrawalId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: WithdrawalRequest = await response.json();
      setWithdrawal(data);
      setAdminNotes(data.admin_notes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch withdrawal details');
    } finally {
      setLoading(false);
    }
  }, [withdrawalId]);

  useEffect(() => {
    if (isOpen && withdrawalId) {
      fetchWithdrawalDetails();
    }
  }, [isOpen, withdrawalId, fetchWithdrawalDetails]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${ADMIN_API_BASE_URL}/withdrawals/${withdrawalId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_notes: adminNotes
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      setShowApprovalModal(false);
      onWithdrawalUpdated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve withdrawal');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason || (rejectionReason === 'Other (specify)' && !customRejectionReason.trim())) {
      setError('Please provide a rejection reason');
      return;
    }

    setRejecting(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const finalReason = rejectionReason === 'Other (specify)' ? customRejectionReason : rejectionReason;

      const response = await fetch(`${ADMIN_API_BASE_URL}/withdrawals/${withdrawalId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rejection_reason: finalReason,
          admin_notes: adminNotes
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setShowRejectionModal(false);
      onWithdrawalUpdated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject withdrawal');
    } finally {
      setRejecting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getKYCStatusBadge = (status: string) => {
    const statusConfig = {
      verified: { color: 'bg-green-100 text-green-800', icon: FaCheckIcon },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaClockIcon },
      rejected: { color: 'bg-red-100 text-red-800', icon: FaTimesIcon },
      not_submitted: { color: 'bg-gray-100 text-gray-800', icon: FaExclamationIcon },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_submitted;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
      </span>
    );
  };

  const canApprove = () => {
    if (!withdrawal || withdrawal.status !== 'pending') return false;
    if (!withdrawal.user_details) return true; // If no user details, allow approval
    return withdrawal.user_details.kyc_status === 'verified' && 
           withdrawal.user_details.withdrawable_balance >= withdrawal.requested_amount;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Review Withdrawal Request
            </h2>
            {withdrawal && (
              <p className="text-sm text-gray-600 mt-1">
                Request ID: {withdrawal.id}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimesIcon className="w-6 h-6" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <FaExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {withdrawal && !loading && (
          <div className="space-y-6">
            {/* KYC Status Check - CRITICAL */}
            <div className="mb-6 border-2 border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3 text-gray-800 flex items-center">
                🔒 KYC Status Check (CRITICAL)
              </h3>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
                    withdrawal.user_details?.kyc_status === 'verified' 
                      ? 'bg-green-100 text-green-700' 
                      : withdrawal.user_details?.kyc_status === 'pending' 
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {withdrawal.user_details?.kyc_status === 'verified' && '✅ Verified'}
                    {withdrawal.user_details?.kyc_status === 'pending' && '🟡 Pending Review'}
                    {withdrawal.user_details?.kyc_status === 'rejected' && '❌ Rejected'}
                    {!withdrawal.user_details?.kyc_status && '❌ Not Verified'}
                  </div>
                  
                  {withdrawal.user_details?.kyc_status !== 'verified' && (
                    <div className="text-red-600 text-sm font-medium">
                      ⚠️ Withdrawal cannot be processed until KYC is complete
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => {
                    // Open KYC review modal or navigate to KYC section
                    if (withdrawal.user_details?.id) {
                      window.open(`/kyc-review/${withdrawal.user_details.id}`, '_blank');
                    }
                  }}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FaEyeIcon className="w-4 h-4 mr-2" />
                  View KYC Documents
                </button>
              </div>
              
              {withdrawal.user_details?.kyc_status !== 'verified' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <FaExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">
                        <strong>Action Required:</strong> User KYC must be verified before processing this withdrawal. 
                        Click "View KYC Documents" to review and approve the user's KYC submission.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.user_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.user_email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mobile</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.user_mobile}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.user_id}</p>
                </div>
              </div>
            </div>

            {/* Withdrawal Details */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Withdrawal Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-700">Requested Amount</label>
                  <p className="mt-1 text-2xl font-bold text-blue-600">
                    {formatCurrency(withdrawal.requested_amount)}
                  </p>
                </div>
                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-700">TDS (30%)</label>
                  <p className="mt-1 text-lg font-medium text-red-600">
                    {formatCurrency(withdrawal.tds_amount)}
                  </p>
                </div>
                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-700">Net Amount</label>
                  <p className="mt-1 text-2xl font-bold text-green-600">
                    {formatCurrency(withdrawal.net_amount)}
                  </p>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Request Date:</strong> {formatDate(withdrawal.request_date)}</p>
                <p><strong>Processing Time:</strong> 24-48 hours (working days)</p>
              </div>
            </div>

            {/* Bank Account Details */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Bank Account Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Holder</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.bank_account_details.account_holder_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.bank_account_details.bank_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Number</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.bank_account_details.masked_account}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                  <p className="mt-1 text-sm text-gray-900">{withdrawal.bank_account_details.ifsc_code}</p>
                </div>
              </div>
            </div>

            {/* KYC Status Check */}
            {withdrawal.user_details && (
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Verification Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">KYC Status</label>
                    <div className="mt-1">
                      {getKYCStatusBadge(withdrawal.user_details.kyc_status)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Withdrawable Balance</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatCurrency(withdrawal.user_details.withdrawable_balance)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Status</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">
                      {withdrawal.user_details.account_status}
                    </p>
                  </div>
                </div>
                
                {withdrawal.user_details.kyc_status !== 'verified' && (
                  <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-md">
                    <div className="flex">
                      <FaExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">
                          <strong>Warning:</strong> User KYC is not verified. Withdrawal cannot be processed until KYC is completed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {withdrawal.user_details.withdrawable_balance < withdrawal.requested_amount && (
                  <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-md">
                    <div className="flex">
                      <FaExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">
                          <strong>Warning:</strong> Insufficient withdrawable balance. User has {formatCurrency(withdrawal.user_details.withdrawable_balance)} available.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Status Indicator */}
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3 text-gray-800">Withdrawal Status</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
                    withdrawal.status === 'pending' 
                      ? 'bg-yellow-100 text-yellow-700' 
                      : withdrawal.status === 'approved' 
                      ? 'bg-green-100 text-green-700'
                      : withdrawal.status === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {withdrawal.status === 'pending' && '🟡 Pending Review'}
                    {withdrawal.status === 'approved' && '✅ Approved'}
                    {withdrawal.status === 'rejected' && '❌ Rejected'}
                    {withdrawal.status === 'processing' && '⏳ Processing'}
                    {withdrawal.status === 'completed' && '✅ Completed'}
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    Request ID: <span className="font-mono font-medium">{withdrawal.id}</span>
                  </div>
                </div>
                
                {withdrawal.processed_at && (
                  <div className="text-sm text-gray-600">
                    Processed: {formatDate(withdrawal.processed_at)}
                  </div>
                )}
              </div>
            </div>

            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Add internal notes about this withdrawal request..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
              <button
                onClick={() => setShowApprovalModal(true)}
                className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
                  canApprove()
                    ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                }`}
                disabled={!canApprove() || approving}
              >
                <FaCheckIcon />
                Approve Withdrawal
              </button>
              <button
                onClick={() => setShowRejectionModal(true)}
                className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={rejecting || withdrawal.status !== 'pending'}
              >
                <FaTimesIcon />
                Reject Withdrawal
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approval Confirmation Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Approval</h3>
              
              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  Are you sure you want to approve this withdrawal of{' '}
                  <span className="font-semibold text-green-600">
                    {formatCurrency(withdrawal?.requested_amount || 0)}
                  </span>{' '}
                  for <span className="font-semibold">{withdrawal?.user_name}</span>?
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  This will initiate payout to the user's bank account.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approving ? 'Approving...' : 'Confirm Approval'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Withdrawal</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select a reason</option>
                  {rejectionReasons.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {rejectionReason === 'Other (specify)' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Reason
                  </label>
                  <textarea
                    value={customRejectionReason}
                    onChange={(e) => setCustomRejectionReason(e.target.value)}
                    rows={3}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Please specify the rejection reason..."
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRejectionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectionReason || (rejectionReason === 'Other (specify)' && !customRejectionReason.trim()) || rejecting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalReviewModal; 